import React, { createContext, useState, useContext, useLayoutEffect, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { serverGet, serverPost } from "../../middleware/http";
import { apiConst, apptMethodConst } from "../constant/constant";
import { ecnSession, getAuthProfile, getMemberId, getMsg, getSearchParams, hasKeys, isArray } from "../utils/validation";
import { useProfile } from "../utils/profileContext";
import { useDoctor } from "./DoctorContext";
import { applyThemeVars, markThemeReady } from "../utils/themeCache";
import { handoffGet } from "../utils/ssoHandoff";

const AccountSettingContext = createContext(null);

export const useAccountSettings = () => {
    return useContext(AccountSettingContext);
};

const baseFontSizes = {
    h1: 32,
    h2: 28,
    h3: 24,
    h4: 20,
    h5: 18,
    h6: 16,
    p: 16,
    span: 16,
    a: 16,
    li: 16,
    label: 16,
    small: 14,
    pre: 16,
    code: 16,
    sub: 12,
    sup: 12,
    blockquote: 18,
    btn: 16,
    div: 16,
    input: 16,
    textarea: 16,
    select: 16,
    "select-option": 16,
};
function updateRootFontSizes(value) {
    if (value == 1) {
        const existingStyle = document.getElementById("dynamic-font-sizes");
        if (existingStyle) {
            existingStyle.parentNode.removeChild(existingStyle);
        }
        return;
    }

    let cssText = ":root {\n";
    for (const tag in baseFontSizes) {
        if (baseFontSizes.hasOwnProperty(tag)) {
            const newSize = baseFontSizes[tag] * value;
            cssText += `  --${tag}-font-size: ${newSize}px;\n`;
        }
    }
    cssText += "}\n\n";

    cssText += _cssText;

    let styleTag = document.getElementById("dynamic-font-sizes");
    if (!styleTag) {
        styleTag = document.createElement("style");
        styleTag.id = "dynamic-font-sizes";
        document.head.appendChild(styleTag);
    }

    styleTag.innerHTML = cssText;
}

const partnerCodes = "VRF";

const accountSettingCode = "DTF,DIPR,CUR,PMTD,GWF,URS,ENSP,ENJN,BECON,AFCON,VCP,BNWTP,CPI,SCL,PHF,PMA,ECI,PFS,PIF,FUA,CIA,ACG,FCI,HMP,AAA,DCS,PCW,ADN,SNP,CBT,PSR,PAT,DAS,AMS,SNP,ADN,DUS,DCP,DUP,DUL,VRL,LUR,SBM,DBK,SCD,ELR,ERU,SCM,PFE,EEI,LUR,LRG,PDO,ECPY,EEVT,ETCF,TMZ,CPYA,PTY,MRL,UAP,URQ,LPI,LQR,U247";

// Setting codes for the `setting/getsettingoptions` (dropdown/option values) fetch.
const settingOptionCodes = "PMTD,PFS,PAT";

// Group codes for the `category/get/` fetch (includes cateogryGroupCodes' codes too).
const categoryGroupCodes = "schedule_duration,encounter_mode,medicaldoc_type,relationship_type,gender_type,identification_type,booking_type,schedulerecurringfrequency_type,weekdays_type,weekordinal_type,communication_mode,common_status,doc_type";
// Subset of categoryGroupCodes used to derive cateogrySettings (see applyCategoryResponse below).
const cateogryGroupCodes = "schedule_duration,encounter_mode,booking_type,schedulerecurringfrequency_type,weekdays_type,weekordinal_type,communication_mode,gender_type,common_status";

// Setting codes for the account host/theme (`basicaccount/gethostinfo`) fetch.
const hostSettingCodes = "HRC,HPC,FTC,DFP";

export const AccountSettingsProvider = ({ children }) => {
    const [account, setAccount] = useState(null);
    const [isAuth, setisAuth] = useState(null);
    const [accountSettings, setAccountSettings] = useState(null);
    const [accountDate, setAccountDate] = useState(null);
    const [showAccPricing, setShowAccPricing] = useState(false);
    const [AccountCurrency, setAccountCurrency] = useState(null);
    const [cateogrySettings, setCateogrySettings] = useState(null);
    const [categoryDetails, setCategoryDetails] = useState(null);
    const [settingOptions, setSettingOptions] = useState(null);
    const [isGWF, setisGWF] = useState(false);
    const [patientMinimumAge, setPatientMinimumAge] = useState("");
    const [isPediatricOnly, setIsPediatricOnly] = useState(false);
    const [isUrgentCare247, setIsUrgentCare247] = useState(false);
    const [conditionValue, setConditionValue] = useState(null); // Reasonn page conditions
    const [conditionDocLength, setConditionDocLength] = useState(null); // Reasonn page conditions
    const [schedulePricing, setSchedulePricing] = useState(null);
    const [accountModules, setAccountModules] = useState(null);
    const [calenderWeeks, setCalenderWeeks] = useState(null);
    const [accountCountry, setAccountCountry] = useState(null);
    const [accountCountryRes, setAccountCountryRes] = useState(null);
    const [countryList, setCountryList] = useState(null);
    const [homeUrl, setHomeUrl] = useState(null);
    const [errMsg, setErrMsg] = useState(null);
    const [fontSize, setFontSize] = useState();
    const [advanceNoticePeriod, setAdvanceNoticePeriod] = useState();
    const [discountPrice, setDiscountPrice] = useState("");
    const [serviceName, setServiceName] = useState("");
    const [extAppRedirectUrl, setExtAppRedirectUrl] = useState("");
    const [partnerSettings, setPartnerSettings] = useState(null);
    const [accountPartners, setAccountPartners] = useState(null);
    const [accountModulesLoaded, setAccountModulesLoaded] = useState(false);
    const [accountPartnersLoaded, setAccountPartnersLoaded] = useState(false);
    // null = not yet checked, distinct from a resolved `false`.
    const [elgPartnerEnabled, setElgPartnerEnabled] = useState(null);
    // PostHog key/host (useAnalytics.js) -- its own standalone request, not part of getClientAccountBootstrap().
    const [analyticsConfig, setAnalyticsConfig] = useState(null);
    // Google Tag Manager partner payload (useAnalytics.js).
    const [gtmPartner, setGtmPartner] = useState(null);
    const [gtmPartnerLoaded, setGtmPartnerLoaded] = useState(false);

    const location = useLocation();
    const { handleAuth } = useProfile();
    const { doctorContext } = useDoctor();
    const fetchingRef = useRef({ accountModules: false, accountPartners: false, categoryDetails: false, settingOptions: false, accountSettings: false, elgPartner: false, accountCountry: false, resCode: false, countryList: false, host: false, gtmPartner: false, analyticsConfig: false });
    // In-flight promise refs so concurrent callers await the same fetch instead of firing a
    // duplicate one -- one per fetchingRef flag above.
    const partnersPromiseRef = useRef(null);
    const categoryPromiseRef = useRef(null);
    const settingOptionsPromiseRef = useRef(null);
    const accountSettingsPromiseRef = useRef(null);
    const accountModulesPromiseRef = useRef(null);
    const elgPartnerPromiseRef = useRef(null);
    const accountCountryPromiseRef = useRef(null);
    const resCodePromiseRef = useRef(null);
    const countryListPromiseRef = useRef(null);
    const hostPromiseRef = useRef(null);
    const gtmPartnerPromiseRef = useRef(null);
    const analyticsConfigPromiseRef = useRef(null);
    // Holds each field's resolved payload, mirrored from state and read at call time.
    const resolvedRef = useRef({});

    // Applies a getaccountsettings-shaped response -- shared by getAccountSetting() and getClientAccountBootstrap()'s settings field.
    const applySettingsResponse = (data) => {
        if (!Array.isArray(data)) return;
        resolvedRef.current.accountSettings = data;
        const dateSetting = data?.find((em) => em?.settingCode == "DTF");
        if (dateSetting) setAccountDate(dateSetting);
        const priceSetting = data?.find((em) => em?.settingCode == "DIPR" && em?.value == "1");
        if (priceSetting) setShowAccPricing(true);
        const account_currency = data?.find((em) => em?.settingCode == "CUR");
        if (account_currency) setAccountCurrency(account_currency);
        const gwf = data?.find((em) => em?.settingCode == "GWF" && em?.value == "1"); //Gaya weelness workflow
        if (gwf) setisGWF(true);
        const pma = data?.find((em) => em?.settingCode == "PMA")?.value; // patient minimum age
        if (pma) setPatientMinimumAge(parseInt(pma));
        const pdo = data?.find((em) => em?.settingCode == "PDO")?.value;
        setIsPediatricOnly(pdo === "1");
        const u247 = data?.find((em) => em?.settingCode == "U247")?.value;
        setIsUrgentCare247(u247 === "1");
        const reasonPageCondition = data?.find((em) => em?.settingCode == "FCI")?.value; // Reason doc
        if (reasonPageCondition) setConditionValue(reasonPageCondition);
        const maxDocLength = data?.find((em) => em?.settingCode == "HMP")?.value; // condition  maximum docs
        if (maxDocLength) setConditionDocLength(maxDocLength);
        const _calendarWeeks = data?.find((em) => em?.settingCode == "PCW")?.value;
        if (_calendarWeeks) setCalenderWeeks(_calendarWeeks);

        if (data?.find((em) => em?.settingCode == "SNP")?.value == "1") {
            const _advanceNoticePeriod = data?.find((em) => em?.settingCode == "ADN")?.value;
            if (parseInt(_advanceNoticePeriod)) setAdvanceNoticePeriod(parseInt(_advanceNoticePeriod));
        }

        const _extAppRedirectUrl = data?.find((em) => em?.settingCode == "ELR")?.value;
        if (_extAppRedirectUrl) setExtAppRedirectUrl(_extAppRedirectUrl);

        setAccountSettings(data);
    };

    const getAccountSetting = async () => {
        try {
            if (isArray(resolvedRef.current.accountSettings)) {
                return resolvedRef.current.accountSettings;
            }

            if (isArray(accountSettings)) {
                return accountSettings;
            }

            if (fetchingRef.current.accountSettings) {
                return await accountSettingsPromiseRef.current;
            }

            fetchingRef.current.accountSettings = true;
            accountSettingsPromiseRef.current = (async () => {
                try {
                    const res = await serverGet(`${apiConst.getaccountsettings}/-/${encodeURIComponent(accountSettingCode)}`);
                    if (res?.status == 1 && Array.isArray(res?.data)) {
                        applySettingsResponse(res.data);
                        return res.data;
                    }
                } catch (e) {
                    console.error(e);
                } finally {
                    fetchingRef.current.accountSettings = false;
                }
            })();
            return await accountSettingsPromiseRef.current;
        } catch (e) {
            console.error(e);
        }
    };

    const applyCountryResponse = (data) => {
        resolvedRef.current.accountCountry = data;
        setAccountCountry(data?.isdCode);
        setAccountCountryRes(data);
    };

    const getAccountCountry = async () => {
        try {
            if (resolvedRef.current.accountCountry) return resolvedRef.current.accountCountry;

            if (accountCountryRes) return accountCountryRes;

            if (fetchingRef.current.accountCountry) {
                return await accountCountryPromiseRef.current;
            }

            fetchingRef.current.accountCountry = true;
            accountCountryPromiseRef.current = (async () => {
                try {
                    const res = await serverGet(`${apiConst.getAccountCountry}`);
                    if (res?.status == 1) {
                        applyCountryResponse(res?.data);
                        return res?.data;
                    }
                } catch (e) {
                    console.log(e);
                } finally {
                    fetchingRef.current.accountCountry = false;
                }
            })();
            return await accountCountryPromiseRef.current;
        } catch (e) {
            console.log(e);
        }
    };

    const applyCountryListResponse = (data) => {
        resolvedRef.current.countryList = data;
        setCountryList(data);
    };

    const getCountryList = async () => {
        try {
            if (isArray(resolvedRef.current.countryList)) return resolvedRef.current.countryList;

            if (isArray(countryList)) return countryList;

            if (fetchingRef.current.countryList) {
                return await countryListPromiseRef.current;
            }

            fetchingRef.current.countryList = true;
            countryListPromiseRef.current = (async () => {
                try {
                    const res = await serverGet(`${apiConst.getCountryList}`);
                    if (res?.status == 1 && isArray(res?.data)) {
                        applyCountryListResponse(res.data);
                        return res.data;
                    }
                } catch (e) {
                    console.log(e);
                } finally {
                    fetchingRef.current.countryList = false;
                }
            })();
            return await countryListPromiseRef.current;
        } catch (e) {
            console.log(e);
        }
    };

    // Applies a gethostinfo-shaped response -- shared by initAccount() and getClientAccountBootstrap()'s host field.
    const applyHostResponse = (data) => {
        resolvedRef.current.host = data;
        document.title = data?.name || "";
        let codes = (data?.portalSettings?.filter((obj) => obj.code === "THM")[0]?.value || "")?.split("~");

        if (codes?.length >= 4) {
            var styles = {
                "primary-color": codes[0],
                "accent-color": codes[1],
                "primary-color-rgb": codes[2],
                "accent-color-rgb": codes[3],
                "bs-body-font-family": !codes[4] ? "'Nunito', sans-serif" : codes[4].split("|")[1],
            };

            let _headerFooterBg;

            if (Array.isArray(codes) && codes.length > 5) {
                _headerFooterBg = JSON.parse(codes[5]);
            } else {
                _headerFooterBg = {};
            }

            applyThemeVars({ ...styles, ...headerAndFooterStyles(_headerFooterBg, styles["primary-color-rgb"]) });
            var url = !codes[4] ? "https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&display=swap" : codes[4].split("|")[0];

            loadCSS(url);

            if (codes.length > 6) {
                setFontSize(parseFloat(codes[6]));
                //document.documentElement.style.setProperty("--global-font-size", `${codes[6]}em`);
            }
        } else {
            markThemeReady();
        }

        setAccount(data);
        setFavicon(data?.favicon);
    };

    // `basicaccount/gethostinfo` fetch -- fallback for getClientAccountBootstrap()'s host field.
    const initAccount = async () => {
        try {
            if (resolvedRef.current.host) return resolvedRef.current.host;

            if (account) return account;

            if (fetchingRef.current.host) {
                return await hostPromiseRef.current;
            }

            fetchingRef.current.host = true;
            hostPromiseRef.current = (async () => {
                try {
                    const res = await serverGet(`${apiConst.gethost}?scodes=${encodeURIComponent(hostSettingCodes)}`);
                    if (res?.status == 1) {
                        applyHostResponse(res.data);
                        return res.data;
                    }
                } catch (e) {
                    console.log(e);
                } finally {
                    fetchingRef.current.host = false;
                }
            })();
            return await hostPromiseRef.current;
        } catch (e) {
            console.log(e);
        }
    };

    const loadCSS = (href) => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        document.head.appendChild(link);
    };

    const headerAndFooterStyles = (value, defaultValue) => {
        const returnJoinvalues = (_value) => {
            if (Array.isArray(_value) && _value?.length == 3) {
                return _value?.join(",");
            }
            return false;
        };

        const styles = {
            "header-bg-color-rgb": returnJoinvalues(value?.headerBGRGB) || "255,255,255",
            "header-color": returnJoinvalues(value?.headerFontRGB) || "",
            "footer-bg-color-rgb": returnJoinvalues(value?.footerBGRGB) || defaultValue,
            "footer-color": returnJoinvalues(value?.footerFontRGB) || "255,255,255",
        };

        return styles;
    };

    const setFavicon = (path) => {
        const link = document.querySelector("link[rel~='icon']");
        let _url = `/favicon.png`;
        if (path) {
            _url = `/doc/getimage?url=${encodeURIComponent(path)}`;
        }
        if (!link) {
            const newLink = document.createElement("link");
            newLink.rel = "icon";
            newLink.href = _url;
            document.head.appendChild(newLink);
        } else {
            link.href = _url;
        }
    };

    const getAccountsettingValue = (arr, code) => {
        if (Array.isArray(arr)) {
            return arr?.find((list) => list?.settingCode == code)?.value;
        }
    };

    const getAuth = async () => {
        try {
            const res = await serverGet(apiConst.getauthprofile);
            if (res?.status == 1) {
                if (res?.data?.auth) {
                    await getMemberId();
                    getAuthProfile.saveProfileAuth();
                    handleAuth(true);
                } else {
                    getAuthProfile.clearProfileAuth();
                }
                setisAuth(res?.data?.auth);
            }
        } catch (error) {
            console.error(error);
        }
    };

    // Derives cateogrySettings from whatever just resolved categoryDetails.
    const applyCategoryResponse = (data) => {
        resolvedRef.current.categoryDetails = data;
        setCategoryDetails(data);
        setCateogrySettings(getCategoryByGroup(cateogryGroupCodes, data));
    };

    // Centralized `category/get/` fetch, shared by every former standalone caller.
    const getCategoryDetails = async () => {
        try {
            if (isArray(resolvedRef.current.categoryDetails)) {
                return resolvedRef.current.categoryDetails;
            }

            if (isArray(categoryDetails)) {
                return categoryDetails;
            }

            if (fetchingRef.current.categoryDetails) {
                return await categoryPromiseRef.current;
            }

            fetchingRef.current.categoryDetails = true;
            categoryPromiseRef.current = (async () => {
                try {
                    const res = await serverGet(`${apiConst.getcateogry}${categoryGroupCodes}`);
                    if (res?.status == 1 && isArray(res?.data)) {
                        applyCategoryResponse(res.data);
                        return res.data;
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    fetchingRef.current.categoryDetails = false;
                }
            })();
            return await categoryPromiseRef.current;
        } catch (error) {
            console.log(error);
        }
    };

    // Filters the shared categoryDetails by one or more comma-separated group codes.
    const getCategoryByGroup = (groupNames, source = categoryDetails) => {
        if (!groupNames || !isArray(source)) return [];
        const groups = groupNames.split(",").map((g) => g.trim()).filter(Boolean);
        return source.filter((item) => groups.includes(item?.group));
    };

    const applySettingOptionsResponse = (data) => {
        resolvedRef.current.settingOptions = data;
        setSettingOptions(data);
    };

    // Centralized `setting/getsettingoptions` fetch, shared by every former standalone caller.
    const getSettingOptionsDetails = async () => {
        try {
            if (isArray(resolvedRef.current.settingOptions)) {
                return resolvedRef.current.settingOptions;
            }

            if (isArray(settingOptions)) {
                return settingOptions;
            }

            if (fetchingRef.current.settingOptions) {
                return await settingOptionsPromiseRef.current;
            }

            fetchingRef.current.settingOptions = true;
            settingOptionsPromiseRef.current = (async () => {
                try {
                    const res = await serverGet(`${apiConst.getsettingoptions}/${settingOptionCodes}`);
                    if (res?.status == 1 && isArray(res?.data)) {
                        applySettingOptionsResponse(res.data);
                        return res.data;
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    fetchingRef.current.settingOptions = false;
                }
            })();
            return await settingOptionsPromiseRef.current;
        } catch (error) {
            console.log(error);
        }
    };

    // Skips the network call only when the next call's param signature is byte-identical to the last one.
    const pricingCacheRef = useRef({ key: null, result: null });

    // forceRefresh bypasses the memoization above for callers that need a guaranteed-fresh price.
    const getSchedulePricing = async (forceRefresh = false) => {
        try {
            const specialityId = getSearchParams("spid");

            if (!specialityId && !getSearchParams("planid")) {
                setSchedulePricing(null);
                return null;
            }

            const session = ecnSession();

            const typeId = getSearchParams("ftypeid") ?? 1;
            const planId = session?.planId || getSearchParams("planid");
            const memberId = getSearchParams("memberid");
            const encounterId = getSearchParams("eid");
            const durationId = getSearchParams("did");
            const encounterModeId = getSearchParams("emid");
            const encounterMethod = apptMethodConst.types[getSearchParams("brtype")] ?? "";
            const serviceId = getSearchParams("serviceid");
            const groupPlanId = session?.groupPlanId || getSearchParams("gpid");
            const planDetailId = session?.planDetailId;
            const scheduleId = getSearchParams("schid");
            const payTypeCode = getSearchParams("paytypecode");

            const apiDiscountId =
                !session?.discountId &&
                    getSearchParams("discountId") &&
                    getSearchParams("current") !== "INSURANCE" &&
                    (getSearchParams("current") === "APS"
                        ? !!session?.discountId
                        : true)
                    ? getSearchParams("discountId")
                    : "";

            const discountId = session?.discountId ?? apiDiscountId;

            const params = new URLSearchParams({
                typeid: typeId,
                spid: specialityId || "",
            });

            if (memberId) params.append("memberid", memberId);
            if (discountId) params.append("discount", discountId);
            if (encounterId) params.append("eid", encounterId);
            if (durationId) params.append("did", durationId);
            if (encounterModeId) params.append("emid", encounterModeId);
            if (encounterMethod) params.append("emtdid", encounterMethod);
            if (serviceId) params.append("sid", serviceId);
            if (planId) params.append("pid", planId);
            if (groupPlanId) params.append("gpid", groupPlanId);
            if (planDetailId) params.append("pdid", planDetailId);

            if (
                scheduleId &&
                getSearchParams("discountId") &&
                session?.apiDiscountValue &&
                session?.discountCategoryId === 1
            ) {
                params.append("schid", scheduleId);
            }

            if (payTypeCode) {
                params.append("paytypecode", payTypeCode);

                if (payTypeCode === "MEM") {
                    const patientMemberId =
                        getSearchParams("fmid") || (await getMemberId());

                    if (patientMemberId) {
                        params.append("patientmemberid", patientMemberId);
                    }
                }
            }

            const cacheKey = params.toString();
            if (!forceRefresh && pricingCacheRef.current.key === cacheKey) {
                setSchedulePricing(pricingCacheRef.current.result);
                return pricingCacheRef.current.result;
            }

            const response = await serverGet(`${apiConst.getPrice}?${params}`);

            const pricing =
                response?.status === 1 && response?.data
                    ? response.data
                    : null;

            pricingCacheRef.current = { key: cacheKey, result: pricing };

            setSchedulePricing(pricing);
            return pricing;
        } catch (error) {
            console.error("Error fetching schedule pricing:", error);
            setSchedulePricing(null);
            return null;
        }
    };

    const applyModulesResponse = (data) => {
        resolvedRef.current.accountModules = data;
        setAccountModules(data);
        setAccountModulesLoaded(true);
    };

    const getAccountModules = async (id) => {
        try {
            if (isArray(resolvedRef.current.accountModules)) {
                setAccountModulesLoaded(true);
                return resolvedRef.current.accountModules;
            }

            if (isArray(accountModules)) {
                setAccountModulesLoaded(true);
                return accountModules;
            }

            if (fetchingRef.current.accountModules) {
                return await accountModulesPromiseRef.current;
            }

            fetchingRef.current.accountModules = true;
            accountModulesPromiseRef.current = (async () => {
                try {
                    // Omit id when falsy (e.g. account not yet resolved) rather than appending "undefined".
                    const res = await serverGet(`${apiConst.getAccountModules}${id || ""}`);
                    if (res?.status == 1 && res?.data != null) {
                        applyModulesResponse(res.data);
                        return res.data;
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    fetchingRef.current.accountModules = false;
                }
            })();
            return await accountModulesPromiseRef.current;
        } catch (error) {
            console.error(error);
        }
    };

    const getElgPartnerEnabled = async () => {
        try {
            if (resolvedRef.current.elgPartner !== undefined) {
                return resolvedRef.current.elgPartner;
            }

            if (elgPartnerEnabled !== null) {
                return elgPartnerEnabled;
            }

            if (fetchingRef.current.elgPartner) {
                return await elgPartnerPromiseRef.current;
            }

            fetchingRef.current.elgPartner = true;
            elgPartnerPromiseRef.current = (async () => {
                try {
                    const res = await serverGet(`${apiConst.getAccountPartners}2/ELG`);
                    const enabled = res?.status === 1 && res?.data?.ELG === true;
                    resolvedRef.current.elgPartner = enabled;
                    setElgPartnerEnabled(enabled);
                    return enabled;
                } catch (error) {
                    console.error(error);
                    return false;
                } finally {
                    fetchingRef.current.elgPartner = false;
                }
            })();
            return await elgPartnerPromiseRef.current;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    const applyGtmPartnerResponse = (data) => {
        resolvedRef.current.gtmPartner = data;
        resolvedRef.current.gtmPartnerLoaded = true;
        setGtmPartner(data);
        setGtmPartnerLoaded(true);
    };

    // `setting/getgtmpartner` fetch used by useAnalytics.js -- fallback for getClientAccountBootstrap()'s gtmPartner field.
    const getGtmPartner = async () => {
        try {
            if (resolvedRef.current.gtmPartnerLoaded) return resolvedRef.current.gtmPartner;

            if (gtmPartnerLoaded) return gtmPartner;

            if (fetchingRef.current.gtmPartner) {
                return await gtmPartnerPromiseRef.current;
            }

            fetchingRef.current.gtmPartner = true;
            gtmPartnerPromiseRef.current = (async () => {
                try {
                    const res = await serverGet(apiConst.getGtmPartner);
                    if (res?.status == 1) {
                        applyGtmPartnerResponse(res.data ?? null);
                        return res.data ?? null;
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    fetchingRef.current.gtmPartner = false;
                }
            })();
            return await gtmPartnerPromiseRef.current;
        } catch (error) {
            console.error(error);
        }
    };

    const applyAnalyticsConfigResponse = (data) => {
        resolvedRef.current.analyticsConfig = data;
        setAnalyticsConfig(data);
    };

    // `setting/getanalyticsconfig` fetch used by useAnalytics.js -- standalone, not part of getClientAccountBootstrap().
    const getAnalyticsConfig = async () => {
        try {
            if (resolvedRef.current.analyticsConfig) return resolvedRef.current.analyticsConfig;

            if (analyticsConfig) return analyticsConfig;

            if (fetchingRef.current.analyticsConfig) {
                return await analyticsConfigPromiseRef.current;
            }

            fetchingRef.current.analyticsConfig = true;
            analyticsConfigPromiseRef.current = (async () => {
                try {
                    const res = await serverGet(apiConst.getAnalyticsConfig);
                    if (res?.key) {
                        applyAnalyticsConfigResponse(res);
                        return res;
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    fetchingRef.current.analyticsConfig = false;
                }
            })();
            return await analyticsConfigPromiseRef.current;
        } catch (error) {
            console.error(error);
        }
    };

    const applyResCodeResponse = (data) => {
        resolvedRef.current.resCode = data;
        setErrMsg(data);
        localStorage.setItem("errorMsg", JSON.stringify(data));
    };

    const getResCode = async () => {
        try {
            if (resolvedRef.current.resCode) return resolvedRef.current.resCode;

            if (errMsg) return errMsg;

            if (fetchingRef.current.resCode) {
                return await resCodePromiseRef.current;
            }

            fetchingRef.current.resCode = true;
            resCodePromiseRef.current = (async () => {
                try {
                    const res = await serverGet(apiConst.getresponsecode + "/en-us");
                    if (res?.data != null && res?.status == 1) {
                        applyResCodeResponse(res.data);
                        return res.data;
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    fetchingRef.current.resCode = false;
                }
            })();
            return await resCodePromiseRef.current;
        } catch (error) {
            console.error(error);
        }
    };

    const updateHomeUrl = (url) => {
        if (url) {
            setHomeUrl(url);
        }
    };

    const getPartnerSettings = async () => {
        try {
            if (isArray(partnerSettings?.accountPartners)) return partnerSettings;
            const res = await serverGet(`${apiConst.getPartnerSettings}2/${encodeURIComponent(partnerCodes)}`);
            if (res?.status == 1 && res?.data != null) {
                setPartnerSettings(res?.data);
                return res?.data;
            }
        } catch (error) {
            console.error(error);
        }
    };


    const applyPartnerResponse = (data) => {
        resolvedRef.current.accountPartners = data;
        resolvedRef.current.accountPartnersLoaded = true;
        setAccountPartners(data);
        setAccountPartnersLoaded(true);
    };

    const getAccountPartner = async () => {
        try {
            if (resolvedRef.current.accountPartnersLoaded) return resolvedRef.current.accountPartners;

            if (accountPartnersLoaded) return accountPartners;

            if (hasKeys(accountPartners)) {
                setAccountPartnersLoaded(true);
                return accountPartners;
            }

            if (fetchingRef.current.accountPartners) {
                return await partnersPromiseRef.current;
            }

            fetchingRef.current.accountPartners = true;
            partnersPromiseRef.current = (async () => {
                try {
                    const res = await serverGet(`${apiConst.getAccountPartners}2/${encodeURIComponent(partnerCodes)}`);
                    if (res?.status == 1) {
                        applyPartnerResponse(res.data);
                        return res.data;
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    fetchingRef.current.accountPartners = false;
                }
            })();
            return await partnersPromiseRef.current;
        } catch (error) {
            console.error(error);
        }
    };

    // Aggregates the calls above into one account/clientaccountbootstrap round trip, fired once at
    // app boot. Per-field error retry: any field that comes back with its own error marker is
    // retried via its own individual endpoint only -- not the whole aggregate again.
    const getClientAccountBootstrap = async () => {
        try {
            // Back off if another effect already claimed one of these fields' fetchingRef flag on
            // this same commit -- let each field's own individual fetch proceed instead.
            if (
                fetchingRef.current.accountSettings ||
                fetchingRef.current.accountModules ||
                fetchingRef.current.accountPartners ||
                fetchingRef.current.categoryDetails ||
                fetchingRef.current.settingOptions ||
                fetchingRef.current.accountCountry ||
                fetchingRef.current.resCode ||
                fetchingRef.current.countryList ||
                fetchingRef.current.host ||
                fetchingRef.current.gtmPartner
            ) {
                return;
            }

            fetchingRef.current.accountSettings = true;
            fetchingRef.current.accountModules = true;
            fetchingRef.current.accountPartners = true;
            fetchingRef.current.categoryDetails = true;
            fetchingRef.current.settingOptions = true;
            fetchingRef.current.accountCountry = true;
            fetchingRef.current.resCode = true;
            fetchingRef.current.countryList = true;
            fetchingRef.current.host = true;
            fetchingRef.current.gtmPartner = true;

            const bootstrapPromise = (async () => {
                try {
                    // Parameterless GET -- the code lists this aggregate needs are declared
                    // server-side now (AccountController.GetClientAccountBootstrap), not sent here.
                    // handoffGet: right after an SSO entry this was already fetched (post sign-in)
                    // while the SSO loader was showing -- see ssoHandoff.js. Otherwise a plain fetch.
                    const res = await handoffGet(apiConst.getClientAccountBootstrap);
                    const data = res?.status == 1 ? res?.data : null;

                    const settingsData = !data?.settingsError && Array.isArray(data?.settings) ? data.settings : null;
                    if (settingsData) applySettingsResponse(settingsData);

                    // country/partner are legitimately null on a real, error-free response -- `data`
                    // itself must be checked too, since a total call failure leaves it null.
                    const countryResolved = data != null && !data?.countryError;
                    if (countryResolved) applyCountryResponse(data?.country ?? null);

                    const modulesData = !data?.modulesError && data?.modules != null ? data.modules : null;
                    if (modulesData) applyModulesResponse(modulesData);

                    const partnerResolved = data != null && !data?.partnerError;
                    if (partnerResolved) applyPartnerResponse(data?.partner ?? null);

                    const categoryData = !data?.categoryError && isArray(data?.category) ? data.category : null;
                    if (categoryData) applyCategoryResponse(categoryData);

                    const settingOptionsData = !data?.settingOptionsError && isArray(data?.settingOptions) ? data.settingOptions : null;
                    if (settingOptionsData) applySettingOptionsResponse(settingOptionsData);

                    const resCodeResolved = data != null && !data?.responseCodesError && data?.responseCodes != null;
                    if (resCodeResolved) applyResCodeResponse(data.responseCodes);

                    const countryListData = !data?.countryListError && isArray(data?.countryList) ? data.countryList : null;
                    if (countryListData) applyCountryListResponse(countryListData);

                    const hostResolved = data != null && !data?.hostError && data?.host != null;
                    if (hostResolved) applyHostResponse(data.host);

                    const gtmPartnerResolved = data != null && !data?.gtmPartnerError;
                    if (gtmPartnerResolved) applyGtmPartnerResponse(data?.gtmPartner ?? null);

                    return {
                        settingsData,
                        modulesData,
                        partnerResolved,
                        partnerData: data?.partner ?? null,
                        categoryData,
                        settingOptionsData,
                        countryResolved,
                        countryData: data?.country ?? null,
                        resCodeResolved,
                        resCodeData: data?.responseCodes ?? null,
                        countryListData,
                        hostResolved,
                        hostData: data?.host ?? null,
                        gtmPartnerResolved,
                        gtmPartnerData: data?.gtmPartner ?? null,
                        // Retry flags: true when this field needs its own individual fallback fetch.
                        errors: {
                            settings: !settingsData,
                            country: !countryResolved,
                            modules: !modulesData,
                            partner: !partnerResolved,
                            category: !categoryData,
                            settingOptions: !settingOptionsData,
                            resCode: !resCodeResolved,
                            countryList: !countryListData,
                            host: !hostResolved,
                            gtmPartner: !gtmPartnerResolved,
                        },
                    };
                } catch (error) {
                    console.error(error);
                    return null;
                } finally {
                    fetchingRef.current.accountSettings = false;
                    fetchingRef.current.accountModules = false;
                    fetchingRef.current.accountPartners = false;
                    fetchingRef.current.categoryDetails = false;
                    fetchingRef.current.settingOptions = false;
                    fetchingRef.current.accountCountry = false;
                    fetchingRef.current.resCode = false;
                    fetchingRef.current.countryList = false;
                    fetchingRef.current.host = false;
                    fetchingRef.current.gtmPartner = false;
                }
            })();

            // Each field's promise ref resolves to what that field's individual getX() would.
            accountSettingsPromiseRef.current = bootstrapPromise.then((r) => r?.settingsData);
            accountModulesPromiseRef.current = bootstrapPromise.then((r) => r?.modulesData);
            partnersPromiseRef.current = bootstrapPromise.then((r) => (r?.partnerResolved ? r.partnerData : undefined));
            categoryPromiseRef.current = bootstrapPromise.then((r) => r?.categoryData);
            settingOptionsPromiseRef.current = bootstrapPromise.then((r) => r?.settingOptionsData);
            accountCountryPromiseRef.current = bootstrapPromise.then((r) => (r?.countryResolved ? r.countryData : undefined));
            resCodePromiseRef.current = bootstrapPromise.then((r) => (r?.resCodeResolved ? r.resCodeData : undefined));
            countryListPromiseRef.current = bootstrapPromise.then((r) => r?.countryListData);
            hostPromiseRef.current = bootstrapPromise.then((r) => (r?.hostResolved ? r.hostData : undefined));
            gtmPartnerPromiseRef.current = bootstrapPromise.then((r) => (r?.gtmPartnerResolved ? r.gtmPartnerData : undefined));

            const result = await bootstrapPromise;

            // Fire-and-forget: each retried getX() manages its own state independently.
            if (result?.errors?.settings) getAccountSetting();
            if (result?.errors?.country) getAccountCountry();
            if (result?.errors?.modules) getAccountModules(account?.accountId);
            if (result?.errors?.partner) getAccountPartner();
            if (result?.errors?.category) getCategoryDetails();
            if (result?.errors?.settingOptions) getSettingOptionsDetails();
            if (result?.errors?.resCode) getResCode();
            if (result?.errors?.countryList) getCountryList();
            if (result?.errors?.host) initAccount();
            if (result?.errors?.gtmPartner) getGtmPartner();
        } catch (error) {
            console.error(error);
        }
    };

    const checkPassedTime = async (value) => {
        try {
            if (getSearchParams("brtype") !== "GT6" && getSearchParams("brtype") !== "UMS") {
                if (!value) return false;

                const noticePeriod = doctorContext?.provider?.advanceNoticePeriod ?? 0;
                const givenDateTime = new Date(value + "Z");
                const currentTime = new Date();
                const minAllowedTime = new Date(currentTime.getTime() + noticePeriod * 60 * 60 * 1000);

                if (givenDateTime <= currentTime || (noticePeriod > 0 && givenDateTime < minAllowedTime)) {
                    // 783 = "your selected appointment time has passed" (passed-time scenario).
                    // NOT 323 ("Timeslot is not available"), which is the schedule-save booking conflict.
                    window.alert(getMsg("783"));

                    const url = new URL(window.location.href);
                    const searchParams = new URLSearchParams(url.search);
                    searchParams.delete("ts");
                    searchParams.delete("ad");

                    if (getSearchParams("brtype") == "RW8") { // NOW also include another centralized type 
                        searchParams.delete("memberid");
                    }

                    searchParams.set("current", "COM");

                    const updatedSearch = searchParams.toString() ? `?${searchParams.toString()}` : "";
                    const newURL = `/service${updatedSearch}`;
                    window.location.href = newURL;

                    return false;
                }

            }
            return true;
        } catch (error) {
            console.error("Error in checkPassedTime:", error);
            return false;
        }
    };

    const updateDiscountPrice = (price, discountPrice) => {
        setDiscountPrice({ price: price, isUpdtaed: true, discountPrice: discountPrice });
    };

    const isAccountModulesFetching = () => fetchingRef.current.accountModules;
    const isAccountPartnersFetching = () => fetchingRef.current.accountPartners;

    // Declared first as a useLayoutEffect so it claims the fetchingRef flags before any effect below.
    useLayoutEffect(() => {
        getClientAccountBootstrap();
    }, []);

    useLayoutEffect(() => {
        getAuth();
        getResCode();
    }, []);

    useLayoutEffect(() => {
        if (!accountSettings) {
            const fetchData = async () => {
                try {
                    await getAccountSetting();
                } catch (error) {
                    console.log(error);
                }
            };
            fetchData();
        }
    }, [location]);

    useEffect(() => {
        if (isAuth) {
            getAuthProfile.saveProfileAuth();
        }
    }, [isAuth]);

    useEffect(() => {
        const timerId = setTimeout(() => {
            if (!categoryDetails) {
                getCategoryDetails();
            }
        }, 1000);
        return () => clearTimeout(timerId);
    }, [location]);

    useEffect(() => {
        const timerId = setTimeout(() => {
            if (!settingOptions) {
                getSettingOptionsDetails();
            }
        }, 1000);
        return () => clearTimeout(timerId);
    }, [location]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const fetchData = async () => {
            const promises = [];

            if (!accountModules && account?.accountId) {
                promises.push(getAccountModules(account.accountId));
            }

            if (isAuth && !accountPartners) {
                promises.push(getAccountPartner());
            }

            if (promises.length > 0) {
                await Promise.all(promises);
            }
        };

        fetchData();
    }, [location.pathname, account?.accountId, isAuth]);

    useEffect(() => {
        if (parseFloat(fontSize) > 1) updateRootFontSizes(fontSize);
    }, [fontSize]);

    return (
        <AccountSettingContext.Provider
            value={{
                accountDate,
                showAccPricing,
                AccountCurrency,
                isGWF,
                accountSettings,
                account,
                patientMinimumAge,
                isPediatricOnly,
                isUrgentCare247,
                cateogrySettings,
                categoryDetails,
                getCategoryDetails,
                getCategoryByGroup,
                settingOptions,
                getSettingOptionsDetails,
                conditionValue,
                conditionDocLength,
                schedulePricing,
                setSchedulePricing,
                getSchedulePricing,
                accountModules,
                getAccountModules,
                getAccountSetting,
                calenderWeeks,
                getAccountsettingValue,
                accountCountry,
                updateHomeUrl,
                homeUrl,
                countryList,
                isAuth,
                errMsg,
                advanceNoticePeriod,
                checkPassedTime,
                updateDiscountPrice,
                discountPrice,
                accountCountryRes,
                fontSize,
                setServiceName,
                serviceName,
                extAppRedirectUrl,
                partnerSettings,
                getPartnerSettings,
                accountPartners,
                getAccountPartner,
                isAccountModulesFetching,
                isAccountPartnersFetching,
                accountModulesLoaded,
                accountPartnersLoaded,
                elgPartnerEnabled,
                getElgPartnerEnabled,
                analyticsConfig,
                getAnalyticsConfig,
                gtmPartner,
                getGtmPartner,
            }}
        >
            <div>{children}</div>
        </AccountSettingContext.Provider>
    );
};

const _cssText = `
h1, .fs-1, .h1 {
    font-size: var(--h1-font-size) !important;
}

h2, .fs-2, .h2 {
    font-size: var(--h2-font-size) !important;
}

h3, .fs-3, .h3 {
    font-size: var(--h3-font-size) !important;
}

h4, .fs-4, .h4 {
    font-size: var(--h4-font-size) !important;
}

h5, .fs-5, .h5 {
    font-size: var(--h5-font-size) !important;
}

h6, .fs-6, .h6 {
    font-size: var(--h6-font-size) !important;
}

p, span, a, li, label, div {
    font-size: var(--p-font-size) !important;
}

 select {
      font-size: var(--select-font-size) !important;
}

select option {
    font-size: var(--select-option-font-size) !important;
}

small {
    font-size: var(--small-font-size);
}

pre {
    font-size: var(--pre-font-size) !important;
}

code {
    font-size: var(--code-font-size);
}

sub,
sup {
    font-size: var(--sub-font-size);
}

blockquote {
    font-size: var(--blockquote-font-size);
}

input, textarea {
    font-size: var(--input-font-size) !important;
}

input::placeholder, textarea::placeholder {
    font-size: var(--input-font-size) !important;
}

.btn,
.btn-primary,
.btn-secondary,
.btn-success,
.btn-danger,
.btn-warning,
.btn-info,
.btn-light,
.btn-dark {
    font-size: var(--btn-font-size) !important;
}
`;
